import { supabase } from '@/integrations/supabase/client';

// Air Quality Index levels and descriptions
export const AQI_LEVELS = {
  good: { min: 0, max: 50, label: 'Good', color: 'green', description: 'Air quality is satisfactory' },
  moderate: { min: 51, max: 100, label: 'Moderate', color: 'yellow', description: 'Acceptable for most people' },
  unhealthySensitive: { min: 101, max: 150, label: 'Unhealthy for Sensitive Groups', color: 'orange', description: 'Sensitive groups may experience health effects' },
  unhealthy: { min: 151, max: 200, label: 'Unhealthy', color: 'red', description: 'Everyone may experience health effects' },
  veryUnhealthy: { min: 201, max: 300, label: 'Very Unhealthy', color: 'purple', description: 'Health warnings of emergency conditions' },
  hazardous: { min: 301, max: 500, label: 'Hazardous', color: 'maroon', description: 'Everyone should avoid all outdoor exertion' }
};

export interface AirQualityData {
  aqi: number;
  time: {
    s: string;
    tz: string;
    v: number;
  };
  city: {
    name: string;
    url: string;
    geo: [string, string];
  };
  iaqi: {
    pm25?: { v: number };
    pm10?: { v: number };
    o3?: { v: number };
    no2?: { v: number };
    so2?: { v: number };
    co?: { v: number };
    t?: { v: number };
    h?: { v: number };
    p?: { v: number };
    w?: { v: number };
    wg?: { v: number };
  };
  dominentpol?: string;
  forecast?: {
    daily: {
      o3: Array<{ avg: number; day: string; max: number; min: number }>;
      pm10: Array<{ avg: number; day: string; max: number; min: number }>;
      pm25: Array<{ avg: number; day: string; max: number; min: number }>;
      uvi: Array<{ avg: number; day: string; max: number; min: number }>;
    };
  };
}

export interface ProcessedAirQualityData {
  aqi: number;
  level: string;
  color: string;
  description: string;
  cityName: string;
  lastUpdated: string;
  pollutants: {
    pm25?: number;
    pm10?: number;
    o3?: number;
    no2?: number;
    so2?: number;
    co?: number;
  };
  dominantPollutant?: string;
  forecast?: Array<{
    date: string;
    aqi: number;
    pollutant: string;
  }>;
}

// Get AQI level information based on AQI value
export function getAQILevel(aqi: number) {
  if (aqi <= 50) return AQI_LEVELS.good;
  if (aqi <= 100) return AQI_LEVELS.moderate;
  if (aqi <= 150) return AQI_LEVELS.unhealthySensitive;
  if (aqi <= 200) return AQI_LEVELS.unhealthy;
  if (aqi <= 300) return AQI_LEVELS.veryUnhealthy;
  return AQI_LEVELS.hazardous;
}

// Get API token from environment
function getAPIToken(): string | null {
  // First try to get from environment variables
  const token = import.meta.env.VITE_AQICN_API_KEY;
  if (token) return token;
  
  // For demo purposes, you can use 'demo' but it has limited functionality
  return 'demo';
}

// Fetch air quality data by coordinates
export async function fetchAirQualityByCoordinates(lat: number, lon: number): Promise<ProcessedAirQualityData | null> {
  try {
    const token = getAPIToken();
    if (!token) {
      console.error('AQICN API token not found');
      return null;
    }

    // Round coordinates to avoid precision issues
    const roundedLat = Math.round(lat * 1000) / 1000;
    const roundedLon = Math.round(lon * 1000) / 1000;

    const url = `https://api.waqi.info/feed/geo:${roundedLat};${roundedLon}/?token=${token}`;
    console.log('Fetching air quality data from:', url);

    const response = await fetch(url);
    
    if (!response.ok) {
      console.error('AQICN API HTTP error:', response.status, response.statusText);
      return null;
    }

    const data = await response.json();

    if (data.status !== 'ok') {
      console.error('AQICN API error:', data.data);
      return null;
    }

    return processAirQualityData(data.data);
  } catch (error) {
    console.error('Error fetching air quality data by coordinates:', error);
    return null;
  }
}

// Fetch air quality data by city name
export async function fetchAirQualityByCity(cityName: string): Promise<ProcessedAirQualityData | null> {
  try {
    const token = getAPIToken();
    if (!token) {
      console.error('AQICN API token not found');
      return null;
    }

    // Clean and prepare city name
    const cleanCityName = cityName.trim();
    if (!cleanCityName) {
      console.error('Empty city name provided');
      return null;
    }

    const url = `https://api.waqi.info/feed/${encodeURIComponent(cleanCityName)}/?token=${token}`;
    console.log('Fetching air quality data for city:', cleanCityName);

    const response = await fetch(url);
    
    if (!response.ok) {
      console.error('AQICN API HTTP error:', response.status, response.statusText);
      return null;
    }

    const data = await response.json();

    if (data.status !== 'ok') {
      console.error('AQICN API error for city', cleanCityName, ':', data.data);
      return null;
    }

    return processAirQualityData(data.data);
  } catch (error) {
    console.error('Error fetching air quality data by city:', error);
    return null;
  }
}

// Search for air quality stations near coordinates
export async function searchNearbyStations(lat: number, lon: number, radius: number = 50): Promise<any[]> {
  try {
    const token = getAPIToken();
    if (!token) {
      console.error('AQICN API token not found');
      return [];
    }

    const url = `https://api.waqi.info/map/bounds/?latlng=${lat-radius/111},${lon-radius/111},${lat+radius/111},${lon+radius/111}&token=${token}`;
    console.log('Searching nearby stations:', url);

    const response = await fetch(url);
    const data = await response.json();

    if (data.status !== 'ok') {
      console.error('AQICN API error:', data.data);
      return [];
    }

    return data.data || [];
  } catch (error) {
    console.error('Error searching nearby stations:', error);
    return [];
  }
}

// Process raw air quality data into our format
function processAirQualityData(data: AirQualityData): ProcessedAirQualityData {
  const level = getAQILevel(data.aqi);
  
  return {
    aqi: data.aqi,
    level: level.label,
    color: level.color,
    description: level.description,
    cityName: data.city.name,
    lastUpdated: data.time.s,
    pollutants: {
      pm25: data.iaqi?.pm25?.v,
      pm10: data.iaqi?.pm10?.v,
      o3: data.iaqi?.o3?.v,
      no2: data.iaqi?.no2?.v,
      so2: data.iaqi?.so2?.v,
      co: data.iaqi?.co?.v,
    },
    dominantPollutant: data.dominentpol,
    forecast: data.forecast?.daily.pm25?.slice(0, 3).map(item => ({
      date: item.day,
      aqi: item.avg,
      pollutant: 'PM2.5'
    })) || []
  };
}

// Get air quality data for a region
export async function fetchRegionAirQuality(regionId: string): Promise<ProcessedAirQualityData | null> {
  try {
    // First, try to get coordinates from the region data
    const { data: regionData, error } = await supabase
      .from('regions')
      .select('name, country, latitude, longitude')
      .eq('id', regionId) // Try 'id' first
      .single();

    if (error && error.code === '42703') {
      // Column doesn't exist, try alternative column names
      const { data: regionData2, error: error2 } = await supabase
        .from('regions')
        .select('name, country, latitude, longitude')
        .eq('region_id', regionId)
        .single();
      
      if (error2) {
        console.error('Error fetching region data:', error2);
        return null;
      }
      
      return await fetchAirQualityForRegion(regionData2);
    }

    if (error) {
      console.error('Error fetching region data:', error);
      return null;
    }

    return await fetchAirQualityForRegion(regionData);
  } catch (error) {
    console.error('Error fetching region air quality:', error);
    return null;
  }
}

// Helper function to fetch air quality data for a region object
async function fetchAirQualityForRegion(regionData: any): Promise<ProcessedAirQualityData | null> {
  // Strategy 1: If we have coordinates, use them (most reliable)
  if (regionData.latitude && regionData.longitude) {
    console.log(`Trying coordinates for ${regionData.name}: ${regionData.latitude}, ${regionData.longitude}`);
    const coordResult = await fetchAirQualityByCoordinates(
      parseFloat(regionData.latitude), 
      parseFloat(regionData.longitude)
    );
    
    if (coordResult) {
      console.log(`✅ Found air quality data via coordinates for ${regionData.name}`);
      return coordResult;
    }
  }

  // Strategy 2: Try just the city name (works better than "City, Country")
  if (regionData.name) {
    console.log(`Trying city name: ${regionData.name}`);
    const cityResult = await fetchAirQualityByCity(regionData.name);
    
    if (cityResult) {
      console.log(`✅ Found air quality data via city name for ${regionData.name}`);
      return cityResult;
    }
  }

  // Strategy 3: Try some variations of city + country
  if (regionData.name && regionData.country) {
    const variations = [
      regionData.name.toLowerCase(),
      `${regionData.name}, ${getCountryCode(regionData.country)}`,
      `${regionData.name}, ${regionData.country}`,
      regionData.name.replace(/\s+/g, '').toLowerCase()
    ];

    for (const variation of variations) {
      console.log(`Trying variation: ${variation}`);
      const result = await fetchAirQualityByCity(variation);
      
      if (result) {
        console.log(`✅ Found air quality data via variation "${variation}" for ${regionData.name}`);
        return result;
      }
    }
  }

  console.log(`❌ No air quality data found for ${regionData.name}, ${regionData.country}`);
  return null;
}

// Helper function to convert country names to common codes
function getCountryCode(country: string): string {
  const countryMap: { [key: string]: string } = {
    'United States': 'USA',
    'United Kingdom': 'UK',
    'China': 'CN',
    'Japan': 'JP',
    'Germany': 'DE',
    'France': 'FR',
    'Italy': 'IT',
    'Spain': 'ES',
    'Canada': 'CA',
    'Australia': 'AU',
    'Brazil': 'BR',
    'India': 'IN',
    'South Korea': 'KR',
    'Mexico': 'MX',
    'Netherlands': 'NL',
    'Sweden': 'SE',
    'Norway': 'NO',
    'Denmark': 'DK',
    'Finland': 'FI',
    'Switzerland': 'CH',
    'Austria': 'AT',
    'Belgium': 'BE',
    'Poland': 'PL',
    'Czech Republic': 'CZ',
    'Hungary': 'HU',
    'Portugal': 'PT',
    'Greece': 'GR',
    'Turkey': 'TR',
    'Russia': 'RU',
    'South Africa': 'ZA',
    'Egypt': 'EG',
    'Israel': 'IL',
    'Thailand': 'TH',
    'Singapore': 'SG',
    'Malaysia': 'MY',
    'Indonesia': 'ID',
    'Philippines': 'PH',
    'Vietnam': 'VN',
    'New Zealand': 'NZ'
  };
  
  return countryMap[country] || country;
}

// Get color class for AQI level
export function getAQIColorClass(aqi: number): string {
  const level = getAQILevel(aqi);
  switch (level.color) {
    case 'green': return 'text-green-500 bg-green-500/10';
    case 'yellow': return 'text-yellow-500 bg-yellow-500/10';
    case 'orange': return 'text-orange-500 bg-orange-500/10';
    case 'red': return 'text-red-500 bg-red-500/10';
    case 'purple': return 'text-purple-500 bg-purple-500/10';
    case 'maroon': return 'text-red-800 bg-red-800/10';
    default: return 'text-gray-500 bg-gray-500/10';
  }
}

// Get background gradient class for AQI level
export function getAQIGradientClass(aqi: number): string {
  const level = getAQILevel(aqi);
  switch (level.color) {
    case 'green': return 'from-green-500/10 via-green-600/5 to-emerald-500/10';
    case 'yellow': return 'from-yellow-500/10 via-yellow-600/5 to-amber-500/10';
    case 'orange': return 'from-orange-500/10 via-orange-600/5 to-red-500/10';
    case 'red': return 'from-red-500/10 via-red-600/5 to-pink-500/10';
    case 'purple': return 'from-purple-500/10 via-purple-600/5 to-indigo-500/10';
    case 'maroon': return 'from-red-800/10 via-red-900/5 to-purple-800/10';
    default: return 'from-gray-500/10 via-gray-600/5 to-slate-500/10';
  }
}